// ABOUTME: Bottom sheet to pick what goes in a dashboard cell. The left column lists subjects (report types +
// ABOUTME: the warehouse subjects that ship pre-built widgets). The right shows that subject's pre-built widgets
// ABOUTME: as a grid, then its reports as a list. Reports return { reportId, kind, ... }; widgets return { kind:"component", componentKey }.
import { Box, ButtonBase, CircularProgress, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import { tokens } from "../../../../../theme";
import SHModalBottom from "../../../../theme/Modal/SHModalBottom";
import Subheading from "../../../../theme/text/Subheading";
import Text from "../../../../theme/text/Text";
import NumberInput from "../../../../theme/inputs/NumberInput";
import ShRowSkeleton from "../../../../theme/skeleton/ShRowSkeleton";
import { reportTypeOptionKeys } from "../../chartOptions";
import { getReportsForEnvironment, reporthubChartOptions } from "../../reports";
import { useReportHubFilters } from "../../ReportHubFilterContext";
import { useGetBusinessIntelligenceReportsQuery } from "../../../../../newapi/global/businessIntelligence";
import { dashboardCellKinds, dashboardPivotVariants, DEFAULT_PIVOT_N } from "../dashboardEnums";
import { dashboardElements, dashboardElementGroups } from "../dashboardElements";
import Shbutton from "../../../../theme/buttons/Shbutton";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";

const PIVOT_VARIANTS = [
    { value: dashboardPivotVariants.TOP, labelKey: "db_view_top" },
    { value: dashboardPivotVariants.BOTTOM, labelKey: "db_view_bottom" },
    { value: dashboardPivotVariants.MOVERS, labelKey: "db_view_movers" },
    { value: dashboardPivotVariants.FULL, labelKey: "db_view_full" },
];

// The subjects that ship pre-built widgets, in display order — folded into the type list.
const SUBJECTS = Object.values(dashboardElementGroups);

// allowFullPivot: only a full-width body section may hold a FULL pivot (rendered live). Elsewhere FULL is
// hidden and pivots are restricted to bounded views (top/bottom/movers).
export const RhReportPicker = ({ open, onClose, onSelect, allowFullPivot = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";

    const filtersRegistry = useReportHubFilters();

    const { data, isLoading, isFetching } = useGetBusinessIntelligenceReportsQuery();
    const reports = data?.data || [];

    const [selectedType, setSelectedType] = useState("all");
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [selectedComponentKey, setSelectedComponentKey] = useState(null);
    const [variant, setVariant] = useState(dashboardPivotVariants.TOP);
    const [n, setN] = useState(DEFAULT_PIVOT_N);

    // Reset transient state each time the modal opens.
    useEffect(() => {
        if (open) {
            setSelectedType("all");
            setSelectedReportId(null);
            setSelectedComponentKey(null);
            setVariant(dashboardPivotVariants.TOP);
            setN(DEFAULT_PIVOT_N);
        }
    }, [open]);

    // Left-column types: report types that have a saved report, unioned with the pre-built subjects, deduped.
    // The subject values match the report-type values where they overlap, so each subject is a single chip —
    // which also means a subject inherits that report type's `environments`, and a type this environment may
    // not build can't reappear as a widget chip. Subjects with no matching report type are unrestricted.
    const types = useMemo(() => {
        const allTypes = new Set(reporthubChartOptions.map((o) => o.name));
        const allowedTypes = new Set(getReportsForEnvironment(filtersRegistry.environment).map((o) => o.name));

        const present = new Set(reports.map((r) => r.details?.type).filter(Boolean));
        const reportTypes = [...allowedTypes].filter((name) => present.has(name));
        const subjects = SUBJECTS.filter((subject) => !allTypes.has(subject) || allowedTypes.has(subject));

        return [...new Set([...reportTypes, ...subjects])];
    }, [reports, filtersRegistry.environment]);

    const filteredReports = useMemo(
        () => (selectedType === "all" ? reports : reports.filter((r) => r.details?.type === selectedType)),
        [reports, selectedType]
    );

    // The pre-built widgets of the selected subject (registry order) — listed under that subject's reports.
    const subjectWidgets = useMemo(
        () => Object.entries(dashboardElements).filter(([, element]) => element.group === selectedType),
        [selectedType]
    );

    // FULL isn't selectable unless this cell allows it — drop back to a bounded view.
    useEffect(() => {
        if (!allowFullPivot && variant === dashboardPivotVariants.FULL) setVariant(dashboardPivotVariants.TOP);
    }, [allowFullPivot, variant]);

    const visibleVariants = allowFullPivot ? PIVOT_VARIANTS : PIVOT_VARIANTS.filter((v) => v.value !== dashboardPivotVariants.FULL);

    const selectedReport = reports.find((r) => r.id === selectedReportId) || null;
    const isPivot = selectedReport?.details?.reportType === reportTypeOptionKeys.PIVOTTABLE;
    const isFull = variant === dashboardPivotVariants.FULL;

    const handleClose = () => onClose?.();

    const handleAdd = () => {
        // A pre-built widget is picked -> add a component cell.
        if (selectedComponentKey) {
            const element = dashboardElements[selectedComponentKey];
            onSelect?.({ kind: dashboardCellKinds.COMPONENT, componentKey: selectedComponentKey, name: t(element.label) });
            handleClose();
            return;
        }
        if (!selectedReport) return;
        // reportId is the source of truth (the viewer fetches live data); name/type/displayType are denormalized
        // purely for the builder's lightweight preview, so no per-cell query/lookup is needed while editing layout.
        const preview = { name: selectedReport.name, type: selectedReport.details?.type, displayType: selectedReport.details?.displayType };
        const chart = isPivot
            ? {
                  reportId: selectedReport.id,
                  kind: dashboardCellKinds.PIVOT,
                  variant,
                  ...(isFull ? {} : { n }),
                  ...preview,
              }
            : { reportId: selectedReport.id, kind: dashboardCellKinds.CHART, ...preview };
        onSelect?.(chart);
        handleClose();
    };

    const typeChip = (value, label) => {
        const active = selectedType === value;
        return (
            <ButtonBase
                key={value}
                onClick={() => { setSelectedType(value); setSelectedReportId(null); setSelectedComponentKey(null); }}
                sx={{
                    justifyContent: "flex-start",
                    width: 1,
                    px: 2,
                    height: 36,
                    borderRadius: 3,
                    color: active ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] : colors.txt["secondary"],
                    background: active ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100] : "transparent",
                    fontWeight: active ? 700 : 500,
                    "&:hover": { background: active ? theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100] : isDark ? colors.grey[300] : colors.grey[100] },
                }}
            >
                <Text sx={{ color: "inherit", fontWeight: "inherit" }}>{label}</Text>
            </ButtonBase>
        );
    };

    const skeletonColor = isDark ? colors.grey[400] : colors.grey[200];

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const showEmpty = filteredReports.length === 0 && subjectWidgets.length === 0;

    return (
        <SHModalBottom open={open} onClose={handleClose} maxWidth={1280}>

            <Box sx={{ textAlign: "center", paddingY: 4, paddingX: 5, display: "flex", flexDirection: "column", gap: 1 }}>
                <Subheading>{t("db_pick_report")}</Subheading>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    minHeight: 0,
                    gap: 3,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    "@media screen and (max-width: 47.9375em)": { flexDirection: "column" }
                }}
            >
                {/* Left: one chip per subject (report types + pre-built subjects, merged). */}
                <Box sx={{ width: 200, flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.5, minHeight: 0, overflowY: "auto" }}>
                    {typeChip("all", t("db_all_types"))}
                    {types.map((type) => typeChip(type, t(type)))}
                </Box>

                {/* Right: the subject's pre-built widgets as a horizontally-scrolling row (previews, no queries), then its reports as a list. */}
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {isLoading || isFetching ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : showEmpty ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                            <Text light>{t("db_no_reports")}</Text>
                        </Box>
                    ) : (
                        <>
                            {subjectWidgets.length > 0 && (
                            <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 1,mb:3.5 }}>
                                <Subheading>{t("db_premade")}</Subheading>
                                <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1 }}>
                                    {subjectWidgets.map(([key, element]) => {
                                        const active = key === selectedComponentKey;
                                        const Widget = element.Component;
                                        return (
                                            <Box
                                                key={key}
                                                onClick={() => { setSelectedComponentKey(key); setSelectedReportId(null); }}
                                                sx={{
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                    borderRadius: 4,
                                                    overflow: "hidden",
                                                    border: `2px solid ${active ? (isDark ? colors.blue[400] : colors.primary[400]) : isDark ? colors.grey[300] : colors.grey[100]}`,
                                                    background: isDark ? colors.grey[300] : colors.grey[0],
                                                    display:"flex",
                                                    flexDirection:"column",
                                                    boxShadow:`0px 2px 4px ${colors.grey[200]}`
                                                }}
                                            >
                                                {/* Preview — NO queries. Widgets render their REAL component with `preview` (which skips
                                                    its data fetch), so you see the component's own skeleton. Elements that are blank
                                                    without data set `skeletonRows` and show that many ShRowSkeleton rows instead. */}
                                                <Box sx={{flex:1, p: 2, pointerEvents: "none", background: isDark ? colors.grey[200] : colors.grey[100] }}>
                                                    {element.skeletonRows ? (
                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                            {Array.from({ length: element.skeletonRows }, (_, i) => <ShRowSkeleton key={i} />)}
                                                        </Box>
                                                    ) : (
                                                        <ErrorBoundary FallbackComponent={() => null} onError={(e) => console.error(e)}>
                                                            <Widget preview isLoading isFetching />
                                                        </ErrorBoundary>
                                                    )}
                                                </Box>
                                                <Box sx={{ px: 2.5, height: 44, display: "flex", alignItems: "center", borderTop: `1px solid ${isDark ? colors.grey[400] : colors.grey[100]}` }}>
                                                    <Text semibold={active} sx={{ color: active ? (isDark ? colors.blue[400] : colors.primary[400]) : colors.txt["primary"] }}>
                                                        {t(element.label)}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                            )}

                            {filteredReports.length > 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Subheading>{t("db_your_reports")}</Subheading>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {filteredReports.map((report) => {
                                    const active = report.id === selectedReportId;
                                    const pivot = report.details?.reportType === reportTypeOptionKeys.PIVOTTABLE;
                                    return (
                                        <Box
                                            key={report.id}
                                            sx={{
                                                flexShrink: 0,
                                                borderRadius: 4,
                                                overflow: "hidden",
                                                border: `1px solid ${active ? (isDark ? colors.blue[400] : colors.primary[400]) : isDark ? colors.grey[300] : colors.grey[100]}`,
                                                background: active ? (isDark ? colors.blue[100] : colors.primary[100]) : isDark ? colors.grey[300] : colors.grey[0],
                                            }}
                                        >
                                            <ButtonBase
                                                onClick={() => { setSelectedReportId(report.id); setSelectedComponentKey(null); }}
                                                sx={{ justifyContent: "space-between", width: 1, px: 2.5, height: 48 }}
                                            >
                                                <Text semibold={active} sx={{ color: active ? (isDark ? colors.blue[400] : colors.primary[400]) : colors.txt["primary"] }}>
                                                    {report.name}
                                                </Text>
                                                <Box
                                                    sx={{
                                                        px: 1.5,
                                                        height: 22,
                                                        borderRadius: 3,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        background: isDark ? colors.grey[400] : colors.grey[100],
                                                        color: colors.txt["secondary"],
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {t(pivot ? "db_format_pivot" : "db_format_chart")}
                                                </Box>
                                            </ButtonBase>

                                            {/* Pivot restriction — inline in the selected pivot row, with a clear divider */}
                                            {active && pivot && (
                                                <Box
                                                    sx={{
                                                        borderTop: `2px solid ${(isDark ? colors.blue[400] : colors.primary[400])}`,
                                                        background: isDark ? colors.grey[200] : colors.grey[0],
                                                        px: 2.5,
                                                        py: 2,
                                                        display: "flex",
                                                        alignItems: "flex-end",
                                                        gap: 2,
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                        <Text light sx={{ fontSize: 12 }}>{t("db_view_mode")}</Text>
                                                        <ToggleButtonGroup
                                                            exclusive
                                                            value={variant}
                                                            onChange={(e, v) => v !== null && setVariant(v)}
                                                            sx={{ padding: 0.5, borderRadius: 4, background: isDark ? colors.grey[300] : colors.grey[100], height: 36, gap: 0.5 }}
                                                        >
                                                            {visibleVariants.map((opt) => (
                                                                <ToggleButton
                                                                    key={opt.value}
                                                                    value={opt.value}
                                                                    sx={{
                                                                        border: 0,
                                                                        borderRadius: "12px !important",
                                                                        px: 1.5,
                                                                        height: 28,
                                                                        textTransform: "none",
                                                                        fontSize: 13,
                                                                        fontWeight: 500,
                                                                        color: colors.txt["secondary"],
                                                                        "&.Mui-selected": { background: (isDark ? colors.blue[100] : colors.primary[100]), color: (isDark ? colors.blue[400] : colors.primary[400]), fontWeight: 700, "&:hover": { background: (isDark ? colors.blue[100] : colors.primary[100]) } },
                                                                    }}
                                                                >
                                                                    {t(opt.labelKey)}
                                                                </ToggleButton>
                                                            ))}
                                                        </ToggleButtonGroup>
                                                    </Box>
                                                    {!isFull && (
                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                            <Text light sx={{ fontSize: 12 }}>{t("db_rows")}</Text>
                                                            <NumberInput value={n} change={(v) => setN(v)} min={1} max={100} onlyPositive maxWidth={150} />
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                            </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        disabled={!selectedReportId && !selectedComponentKey}
                        isLoading={isLoading}
                        className={"group-btn"} color={"green"}
                        variant="contained"
                        onClick={handleAdd}
                    >{t("add")}</Shbutton>
                </ButtonGroup>
            </Box>

        </SHModalBottom>
    );
};
